/**
 * Cavendish brand style configuration — colors, typography, and prompt rules
 * extracted from the client's "Cavendish.pptx" (slide master colorScheme
 * "Cavendish Rebrand" + fontScheme, layout placeholder geometry, via the
 * OOXML).
 *
 * Mirrors the shape of `evercoreStyleConfig.ts` / `messierStyleConfig.ts`.
 * When Cavendish style is active the prompt below is appended to the system
 * prompt and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: colors come from the master colorScheme "Cavendish Rebrand";
 * fonts from fontScheme "Cavendish Rebrand" (Montserrat for titles AND body).
 * Slide size is A4-landscape (841.9×595.2 pt). Title/content geometry is from
 * the slide master. The logo lives on the slide master, so default AddSlide
 * inherits it.
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "Cavendish Rebrand" colorScheme
// ---------------------------------------------------------------------------

export const CAVENDISH_COLORS = {
  charcoal: "3D3935", // dk1 — titles & body text
  navy: "00263E", // dk2
  lightGray: "E1E4E5", // lt2
  blue: "1D428A", // accent1 — Cavendish signature blue (primary brand / emphasis)
  lightBlue: "7DA1C4", // accent2
  paleBlue: "B8DDE1", // accent3
  brightBlue: "5685FC", // accent4 — also hyperlink color
  gray: "9E9B9A", // accent5
  orange: "F68D2E", // accent6

  ink: "3D3935", // dk1 — titles & body text
  white: "FFFFFF", // lt1

  ruleGray: "9E9B9A", // neutral gray for rule / separator lines
  altRow: "E1E4E5", // light neutral for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when Cavendish style is active. Values map 1:1
 * to the Cavendish slide-master colorScheme + fontScheme.
 */
export const CAVENDISH_THEME_COLORS: ThemeColors = {
  dark1: CAVENDISH_COLORS.ink, // #3D3935 — Text1
  dark2: CAVENDISH_COLORS.navy, // #00263E — Text2
  light1: CAVENDISH_COLORS.white, // #FFFFFF — Background1
  light2: CAVENDISH_COLORS.lightGray, // #E1E4E5 — Background2
  accent1: CAVENDISH_COLORS.blue, // #1D428A
  accent2: CAVENDISH_COLORS.lightBlue, // #7DA1C4
  accent3: CAVENDISH_COLORS.paleBlue, // #B8DDE1
  accent4: CAVENDISH_COLORS.brightBlue, // #5685FC
  accent5: CAVENDISH_COLORS.gray, // #9E9B9A
  accent6: CAVENDISH_COLORS.orange, // #F68D2E
  hyperlink: CAVENDISH_COLORS.brightBlue,
  followedHyperlink: CAVENDISH_COLORS.brightBlue,
  majorFont: "Montserrat",
  minorFont: "Montserrat",
};

/**
 * Ordered series palette. Led by the Cavendish blue (accent1), followed by
 * the orange accent and the remaining distinct theme accents for readable
 * multi-series separation.
 */
export const CAVENDISH_CHART_SERIES_PALETTE = [
  CAVENDISH_COLORS.blue,
  CAVENDISH_COLORS.orange,
  CAVENDISH_COLORS.brightBlue,
  CAVENDISH_COLORS.lightBlue,
  CAVENDISH_COLORS.paleBlue,
  CAVENDISH_COLORS.gray,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from the Cavendish master)
// ---------------------------------------------------------------------------

export const CAVENDISH_SLIDE_DIMENSIONS = { width: 841.9, height: 595.2 } as const;

export const CAVENDISH_POSITIONS = {
  title: { left: 56.2, top: 38.3, width: 729.4, height: 36.8 },
  contentStart: { left: 56.2, top: 104.9 },
  footnote: { left: 56.1, top: 561.5, width: 483.0, height: 24.9 },
} as const;

// Authoritative layout names from the Cavendish master.
export const CAVENDISH_LAYOUT_NAMES = [
  "Cover 1",
  "Cover 2",
  "Cover 3",
  "Cover 4",
  "Cover 5",
  "Cover 6",
  "Cover 7",
  "Agenda",
  "Divider",
  "Divider 2",
  "Divider 3",
  "Divider 4",
  "Title Only",
  "Title and Content",
  "Title, Graphic and Text Box",
  "Tombstones",
  "Title Only Blue",
  "Title, Graphic and Text Box Blue",
  "Bios and Short Case Studies",
  "Bios 3 People",
  "Bios 2 People",
  "Cavendish Teams",
  "Leading transactions",
  "Two Column",
  "Three Column",
  "Four Column",
  "Two Vertical Columns",
  "1/2 Panel, Left Arctic Blue",
  "1/2 Panel, Right Arctic Blue",
  "1/2 Panel, Left Blue",
  "1/2 Panel, Right Blue",
  "1/3 Panel, Right Navy",
  "1/3 Panel, Right Blue",
  "Corporate Finance Case Study",
  "M&A Advisory Case Study",
  "1_M&A Advisory Case Study",
  "2_M&A Advisory Case Study",
  "Single Testimonial",
  "Multiple Testimonials",
  "Back page contact",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Cavendish style is active
// ---------------------------------------------------------------------------

export const CAVENDISH_STYLE_PROMPT = `

## CAVENDISH BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Cavendish.pptx" contains the current investment-banking visual standard. Match the closest relevant precedent for investment highlights, company and market overviews, buyers and buyer profiles, valuation benchmarking, financials, track record, market updates, and sector-specialist pages. Prefer its information-dense grids, title hierarchy, spacing, and source/footer treatment over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Cavendish uses a single Latin typeface throughout:
- Titles, headings, body, bullets, tables, charts: \`"Montserrat"\`
NEVER omit fontFamily when Cavendish style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the Cavendish master colorScheme "Cavendish Rebrand")
Use ONLY these Cavendish brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Charcoal #${CAVENDISH_COLORS.ink}
- Text2 / dark2: Navy #${CAVENDISH_COLORS.navy}
- Background1 / light1: White #${CAVENDISH_COLORS.white}
- Background2 / light2: Light gray #${CAVENDISH_COLORS.lightGray}
- Accent1: Blue #${CAVENDISH_COLORS.blue}
- Accent2: Light blue #${CAVENDISH_COLORS.lightBlue}
- Accent3: Pale blue #${CAVENDISH_COLORS.paleBlue}
- Accent4: Bright blue #${CAVENDISH_COLORS.brightBlue}
- Accent5: Gray #${CAVENDISH_COLORS.gray}
- Accent6: Orange #${CAVENDISH_COLORS.orange}

**Usage:**
- **Primary brand / emphasis**: Blue #${CAVENDISH_COLORS.blue}
- **Secondary emphasis**: Orange #${CAVENDISH_COLORS.orange}
- **Text / ink**: Charcoal #${CAVENDISH_COLORS.ink}
- **Lines & borders**: Gray #${CAVENDISH_COLORS.ruleGray}
- **Background / shading**: Light gray #${CAVENDISH_COLORS.altRow}
- **Chart series palette** (in order): ${CAVENDISH_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with blue (#${CAVENDISH_COLORS.blue}) as the first/primary series color.

### Typography (authoritative — from the Cavendish master)
Slide dimensions: 841.9 × 595.2 pt (A4 landscape).

**Placeholder positions:**
- **Title**: left=56.2, top=38.3, width=729.4, height=36.8 — "Montserrat" 20pt, charcoal (#${CAVENDISH_COLORS.ink}) or navy, left-aligned
- **Content area**: starts at left=56.2, top=104.9
- **Footer / date + page number**: bottom band around top=561 — "Montserrat" ~9pt, gray

**Content text hierarchy (from the master — "Montserrat", charcoal #${CAVENDISH_COLORS.ink}, ~10-12pt body):**
- Keep body text ~10-12pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use bold or the blue accent; bullets otherwise inherit charcoal text.

### Charts (Cavendish style)
- Font: "Montserrat", minimum 8pt
- **Primary data color**: blue (#${CAVENDISH_COLORS.blue}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Cavendish style)
- Header row: navy (#${CAVENDISH_COLORS.navy}) fill with white text, bold
- Body: white fill, charcoal "Montserrat" text (10pt)
- Alternate-row shading: light gray (#${CAVENDISH_COLORS.altRow})
- Borders: thin gray (#${CAVENDISH_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Cavendish style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${CAVENDISH_COLORS.ruleGray}) rule lines for separation when needed
- Connectors: solid gray (#${CAVENDISH_COLORS.ruleGray}) lines
- Page numbers / footer: ~9pt, gray, "Montserrat"

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
